import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, Mail, CheckCircle } from "lucide-react";
import AmmoLogo from "../components/AmmoLogo";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** FastAPI's error detail, which is a string for our own errors and a list of
 *  objects when validation rejects the body. The fallback is passed in rather
 *  than written here, because this file has no business holding an English
 *  sentence of its own. */
function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || fallback;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return fallback;
}

/** The half of the screen that is only decoration, hidden below lg. Written
 *  once rather than pasted into each state of the page. */
function Brand() {
  return (
    <div className="hidden lg:flex lg:w-1/2 relative bg-indigo-800">
      <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
        <div className="w-28 h-28 rounded-full bg-white flex items-center justify-center shadow-lg mb-8">
          <AmmoLogo className="w-20 h-20" />
        </div>
        <h1 className="text-4xl font-bold text-white text-center">AMMO Core</h1>
      </div>
    </div>
  );
}

function LanguageToggle({ language, setLanguage }) {
  return (
    <div className="flex justify-center gap-2 mt-4">
      <button onClick={() => setLanguage("en")} className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${language === "en" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>EN</button>
      <button onClick={() => setLanguage("it")} className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${language === "it" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>IT</button>
    </div>
  );
}

export default function ForgotPassword() {
  const { t, language, setLanguage } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      const status = err.response?.status;
      // This page can be reached without logging in, so the difference between
      // "that address has an account" and "it does not" must not be visible
      // here: anyone could read it off the screen, one address at a time.
      // A server that answered at all has been asked, and the page says so
      // whatever the answer was.
      //
      // Two cases are still reported, because neither reveals anything about
      // the address and both mean the mail genuinely was not sent: being rate
      // limited, and the server failing. A request that never reached a server
      // has no status at all and falls here too.
      if (status && status < 500 && status !== 429) {
        setSent(true);
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail, t("common.actionFailed")));
      }
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex">
        <Brand />
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-3" data-testid="forgot-success-title">
              {t("forgot.sentTitle")}
            </h2>
            <p className="text-slate-500 mb-8 break-words" data-testid="forgot-success-message">
              {t("forgot.sentMessage", { email })}
            </p>
            <Link
              to="/login"
              data-testid="forgot-back-to-login-btn"
              className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("forgot.backToLogin")}
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <Brand />
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <Link
            to="/login"
            data-testid="forgot-back-link"
            className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("forgot.backToLogin")}
          </Link>

          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">{t("forgot.title")}</h2>
          </div>
          <p className="text-slate-500 mb-8">{t("forgot.subtitle")}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="forgot-email" className="text-sm font-medium text-slate-700">
                {t("login.email")}
              </Label>
              <Input
                id="forgot-email"
                data-testid="forgot-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
                className="h-11 border-slate-200 focus:ring-1 focus:ring-indigo-600 focus:border-indigo-600"
              />
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm break-words"
                data-testid="forgot-error"
              >
                {error}
              </motion.div>
            )}

            <Button
              type="submit"
              data-testid="forgot-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-indigo-700 hover:bg-indigo-600 text-white font-medium rounded-lg"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("forgot.submit")}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-slate-400">{t("forgot.hint")}</p>
          <LanguageToggle language={language} setLanguage={setLanguage} />
        </motion.div>
      </div>
    </div>
  );
}
